import { Link, useParams } from "react-router-dom";
import { buildUrl, useApi } from "../api";
import type { PollDetailData } from "../api";
import {
  Card,
  ErrorNote,
  Initiators,
  Loading,
  PartyTag,
  VoteLegend,
  VoteStack,
  formatDate,
  formatNumber,
  htmlToText,
} from "../components";

export default function PollDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, loading, error, reload } = useApi<PollDetailData>(
    id ? buildUrl(`/api/polls/${id}`) : null,
  );

  if (error) return <ErrorNote message={error} onRetry={reload} />;
  if (loading || !data) return <Loading rows={8} />;

  const { poll, fraktionen } = data;

  // Fraktionen, die die Vorlage eingebracht, aber mehrheitlich nicht
  // zugestimmt haben. Das sieht nach Widerspruch aus, ist aber meist keiner --
  // siehe den Hinweis unten.
  const urheberGegen = fraktionen.filter(
    (f) => f.hat_eingebracht && f.majority_vote !== "yes",
  );

  const beschreibung = htmlToText(poll.intro_html);

  const gesamt = fraktionen.reduce(
    (acc, f) => ({
      ja: acc.ja + f.n_yes,
      nein: acc.nein + f.n_no,
      enth: acc.enth + f.n_abstain,
      abw: acc.abw + f.n_no_show,
    }),
    { ja: 0, nein: 0, enth: 0, abw: 0 },
  );

  return (
    <>
      <div className="page-head">
        <p style={{ marginBottom: "0.4rem" }}>
          <Link to="/abstimmungen">← Alle Abstimmungen</Link>
        </p>
        <h1>{poll.label}</h1>
        <p>
          {formatDate(poll.poll_date)} ·{" "}
          <span className={`badge ${poll.accepted ? "yes" : "no"}`}>
            {poll.accepted ? "angenommen" : "abgelehnt"}
          </span>{" "}
          · Ja {formatNumber(gesamt.ja)} · Nein {formatNumber(gesamt.nein)} · Enthaltungen{" "}
          {formatNumber(gesamt.enth)} · nicht abgestimmt {formatNumber(gesamt.abw)}
        </p>
        {poll.vorgang_id ? (
          <p
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexWrap: "wrap",
              marginTop: "0.5rem",
            }}
          >
            <span style={{ color: "var(--ink-3)", fontSize: "0.88rem" }}>
              Eingebracht von
            </span>
            <Initiators urheber={poll.urheber} />
          </p>
        ) : null}
      </div>

      <div className="grid" style={{ gap: "1rem" }}>
        {beschreibung ? (
          <Card
            title="Worum es ging"
            subtitle="Beschreibung von abgeordnetenwatch — hier steht, was tatsächlich zur Abstimmung stand."
          >
            <p style={{ margin: 0, fontSize: "0.94rem" }}>{beschreibung}</p>
          </Card>
        ) : null}

        <Card
          title="Stimmverhalten der Fraktionen"
          subtitle="Anteile innerhalb jeder Fraktion. Eine gespaltene Fraktion ist am Balken sofort zu erkennen."
        >
          <div className="table-wrap" style={{ border: "none" }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: "7.5rem" }}>Fraktion</th>
                  <th>Verteilung</th>
                  <th className="num">Ja</th>
                  <th className="num">Nein</th>
                  <th className="num">Enth.</th>
                  <th className="num">Abw.</th>
                  <th>Mehrheit</th>
                </tr>
              </thead>
              <tbody>
                {fraktionen.map((f) => (
                  <tr key={f.fraction_label ?? "fraktionslos"}>
                    <td>
                      <PartyTag short={f.short_name ?? "fraktionslos"} color={f.color} />
                      {f.hat_eingebracht ? (
                        <div style={{ marginTop: "0.25rem" }}>
                          <span
                            className="pill"
                            title="Diese Fraktion hat die Vorlage eingebracht"
                          >
                            eingebracht
                          </span>
                        </div>
                      ) : null}
                    </td>
                    <td style={{ minWidth: "11rem" }}>
                      <VoteStack row={f} />
                    </td>
                    <td className="num">{f.n_yes}</td>
                    <td className="num">{f.n_no}</td>
                    <td className="num">{f.n_abstain}</td>
                    <td className="num">{f.n_no_show}</td>
                    <td>
                      {f.majority_vote === "gespalten" ? (
                        <span className="pill">gespalten</span>
                      ) : (
                        <span className={`badge ${f.majority_vote === "yes" ? "yes" : "no"}`}>
                          {f.majority_vote === "yes" ? "dafür" : "dagegen"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <VoteLegend />

          {urheberGegen.length > 0 ? (
            <div className="notice" style={{ marginTop: "0.9rem" }}>
              <strong>
                {urheberGegen.map((f) => f.short_name).join(", ")} hat die Vorlage
                eingebracht, aber mehrheitlich nicht zugestimmt.
              </strong>{" "}
              Das ist in der Regel kein Widerspruch: Abgestimmt wird häufig nicht
              über den Antrag selbst, sondern über die Beschlussempfehlung des
              Ausschusses, ihn abzulehnen. Eine Nein-Stimme unterstützt dann den
              ursprünglichen Antrag. Der Titel oben stammt von abgeordnetenwatch und
              beschreibt das Ergebnis, nicht den Antrag. Was genau zur Abstimmung
              stand, steht im Beschreibungstext der Quelle.
            </div>
          ) : null}
        </Card>

        {poll.vorgang_id ? (
          <Card title="Zugrunde liegende Vorlage">
            <h3 style={{ marginBottom: "0.3rem" }}>{poll.vorgang_titel}</h3>
            <p style={{ margin: 0, color: "var(--ink-2)", fontSize: "0.88rem" }}>
              {poll.vorgang_typ}
              {poll.beratungsstand ? ` · Stand: ${poll.beratungsstand}` : ""}
              {poll.dip_id ? (
                <>
                  {" · "}
                  <span className="dip-id">DIP {poll.dip_id}</span>
                </>
              ) : null}
            </p>

            <dl className="meta" style={{ marginBottom: 0 }}>
              <div>
                <dt>Urheber</dt>
                <dd>
                  <Initiators urheber={poll.urheber} />
                </dd>
              </div>
              {poll.sachgebiet && poll.sachgebiet.length > 0 ? (
                <div>
                  <dt>Sachgebiet</dt>
                  <dd style={{ fontSize: "0.85rem" }}>
                    {poll.sachgebiet.filter(Boolean).join(", ")}
                  </dd>
                </div>
              ) : null}
            </dl>
            {poll.vorgang_abstract ? (
              <p style={{ marginTop: "0.7rem", fontSize: "0.92rem" }}>
                {poll.vorgang_abstract}
              </p>
            ) : null}
            {poll.drucksache_nummern && poll.drucksache_nummern.length > 0 ? (
              <p style={{ marginTop: "0.7rem", fontSize: "0.85rem" }}>
                Drucksachen:{" "}
                {/* Die Nutzungsbedingungen des Bundestages verlangen bei
                    amtlichen Werken die Bezeichnung "BT-Drs." samt Nummer. */}
                {poll.drucksache_nummern.map((nr) => (
                  <span key={nr} className="pill" style={{ marginRight: "0.3rem" }}>
                    BT-Drs. {nr}
                  </span>
                ))}
              </p>
            ) : null}
            <p style={{ marginTop: "0.8rem", fontSize: "0.85rem" }}>
              <a
                href={`https://www.abgeordnetenwatch.de/bundestag/21/abstimmungen`}
                target="_blank"
                rel="noreferrer noopener"
              >
                Quelle bei abgeordnetenwatch
              </a>
            </p>
          </Card>
        ) : (
          <div className="notice">
            Dieser Abstimmung ist keine Vorlage zugeordnet. Ohne den Gesetzestext lässt
            sich das Stimmverhalten nicht inhaltlich einordnen.
          </div>
        )}
      </div>
    </>
  );
}
